import React from "react";

import {
  DatePicker,
  Divider,
  Grid,
  InlineAlert,
  Section,
} from "@samskip/frontend-components";
import {
  Control,
  DeepRequired,
  FieldErrorsImpl,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DomesticConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import { getErrorMessage } from "@assets/utilities";
import { getMinReceiptDate } from "@assets/utilities/dateUtilities";
import { WithFormController } from "@components";

interface CalendarSectionProps {
  control: Control<DomesticFormTypes.CreateForm>;
  errors: FieldErrorsImpl<DeepRequired<DomesticFormTypes.CreateForm>>;
  minDeliveryDate: Date;
  onReceiptDateChange: (newReceiptDate: Date | null) => void;
}

const CalendarSection = ({
  control,
  errors,
  minDeliveryDate,
  onReceiptDateChange,
}: CalendarSectionProps) => {
  const minReceiptDate = getMinReceiptDate();
  const { t } = useTranslation();

  const pickupMethod: DomesticConstants.DELIVERY_MODE = useWatch({
    control,
    name: "PickupMethod",
  });
  const dropOffMethod: DomesticConstants.DELIVERY_MODE = useWatch({
    control,
    name: "DropOffMethod",
  });

  const showReceiptDatePicker =
    pickupMethod === DomesticConstants.DELIVERY_MODE.ADDRESS;
  const showDeliveryDatePicker =
    dropOffMethod === DomesticConstants.DELIVERY_MODE.ADDRESS;

  if (!showReceiptDatePicker && !showDeliveryDatePicker) {
    return null;
  }

  return (
    <>
      <Grid mdCol={2}>
        {showReceiptDatePicker && (
          <Section title={t("LABEL_DATE_OF_RECEIPT")}>
            <WithFormController control={control} name="ReceiptDate">
              <DatePicker
                disableWeekends
                errorMessage={getErrorMessage("ReceiptDate", errors)}
                hasError={errors.ReceiptDate != null}
                id="receipt-date"
                label={t("LABEL_PREFERRED_DATE")}
                minDate={minReceiptDate}
                onChange={onReceiptDateChange}
                placeholder={t("PLACEHOLDER_SELECT_RECEIPT_DATE")}
                required
              />
            </WithFormController>
            <InlineAlert>{t("TEXT_RECEIPT_DATE_COST")}</InlineAlert>
          </Section>
        )}

        {showDeliveryDatePicker && (
          <Section
            className="md:col-start-2"
            title={t("LABEL_DATE_OF_DELIVERY")}
          >
            <WithFormController control={control} name="DeliveryDate">
              <DatePicker
                disableWeekends
                errorMessage={getErrorMessage("DeliveryDate", errors)}
                hasError={errors.DeliveryDate != null}
                id="delivery-date"
                label={t("LABEL_PREFERRED_DATE")}
                minDate={minDeliveryDate}
                placeholder={t("PLACEHOLDER_SELECT_DELIVERY_DATE")}
                required
              />
            </WithFormController>
            <InlineAlert>{t("TEXT_DELIVERY_DATE_COST")}</InlineAlert>
          </Section>
        )}
      </Grid>
      <Divider />
    </>
  );
};

export default CalendarSection;
